import React, { useContext, useEffect, useState } from 'react';
import { Modal, Checkbox } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import Cross from 'assets/svg/Cross';
import { colorConstants } from 'config/colorConstants';
import { CustomSelect } from 'CustomComponentCss';
import DownSvg from 'assets/svg/Down';
import { UserContext } from 'contexts/ConfigContext';
import { ConfigKeys } from 'utils/Enums';
import { getConfig } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PSR_COMP_SELECTE_PLANS_DATA_SOURCE_CHANGED } from 'utils/mixpanelEventNames';

type AddScenarioModalProps = {
    visible: boolean;
    closeModal: () => void;
    allScenariosInfo: Array<{ name: string; scenarioId: number; dataSourceType: string }>;
    selectedRowKeys: Array<number>;
    onApplyChange: Function;
};

const AddScenarioModal = ({
    visible,
    closeModal,
    allScenariosInfo,
    selectedRowKeys,
    onApplyChange,
}: AddScenarioModalProps) => {
    const { appConfig } = useContext(UserContext);
    const { dataSources } = getConfig(ConfigKeys.PRICE_SIMULATOR, appConfig);
    const [tempCheckedValues, setTempCheckedValues] = useState<number[]>([]);
    const [availableScenarioNames, setAvailableScenarioNames] = useState<any>([]);
    const [tempAvailableScenarioNames, setTempAvailableScenarioNames] = useState<any>([]);
    const [selectedDataSource, setSelectedDataSource] = useState({ label: '', value: '' });

    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const onApplyChangeHandler = () => {
        onApplyChange(tempCheckedValues);
        closeModal();
        // trackEventMixpanel(PSR_COMP_SELECTE_PLANS_CLICKED, {});
    };

    useEffect(() => {
        setAvailableScenarioNames(allScenariosInfo);
        setTempAvailableScenarioNames(allScenariosInfo);
    }, [allScenariosInfo]);

    useEffect(() => {
        setTempCheckedValues(selectedRowKeys);
        if (selectedRowKeys.length) {
            const currentSourceType =
                allScenariosInfo.find((s: any) => s.scenarioId === selectedRowKeys[0])?.dataSourceType || '';

            setSelectedDataSource(dataSources.find((item: any) => item.label === currentSourceType));
        }
    }, [selectedRowKeys, visible]);

    // on change of data source drop down
    useEffect(() => {
        const filteredScenarios = availableScenarioNames.filter(
            (item: any) => item.dataSourceType === selectedDataSource.label,
        );
        setTempAvailableScenarioNames(filteredScenarios);
        setTempCheckedValues([]);
    }, [selectedDataSource]);

    const onResetClick = () => {
        setTempCheckedValues(selectedRowKeys);
        closeModal();
    };

    const handleCheckboxChange = (checkedList: any) => {
        if (checkedList.length > 5) {
            setShowErrorMessage(true);
            setTimeout(() => {
                setShowErrorMessage(false);
            }, 3000);
            return;
        }
        setTempCheckedValues(checkedList);
    };

    const handleRemoveSelection = (unselectedId: number) => {
        const newSelectedValue = tempCheckedValues.filter((id: any) => id !== unselectedId);
        setTempCheckedValues(newSelectedValue);
    };

    return (
        <StyledModal
            open={visible}
            title="Select Plans"
            onCancel={onResetClick}
            footer={null}
            width={550}
            className="rounded "
        >
            <div className="flex flex-col">
                {showErrorMessage && (
                    <span style={{ color: colorConstants.red }} className="text-danger font-xs font-medium mb-2">
                        You can select upto 5 plans
                    </span>
                )}
                <span className="mr-6 font-greyFont flex  items-center mb-2">
                    <div className="font-xs mr-2 font-medium">
                        <span className="text-fontGray">Data Source</span>
                    </div>
                    <CustomSelect
                        className="dropdown-height  dropdown-width-multi-select overflow-hidden uniqSelSMDataSourceDropdown"
                        suffixIcon={<DownSvg />}
                        value={selectedDataSource?.value || null}
                        options={dataSources}
                        onChange={(val: any, item: any) => {
                            setSelectedDataSource(item);
                            trackEventMixpanel(PSR_COMP_SELECTE_PLANS_DATA_SOURCE_CHANGED, {
                                selectedSource: item.label,
                            });
                        }}
                        placeholder="Select Data Source"
                    />
                </span>
                <div className="border border-borderLinePurpuleAlt rounded overflow-hidden">
                    <div className=" border-b border-borderLinePurpuleAlt p-2.5 bg-bgBackground font-bold text-purpleFont font-xs">
                        Plans
                    </div>
                    {tempAvailableScenarioNames.length > 0 ? (
                        <Checkbox.Group className="w-full" value={tempCheckedValues} onChange={handleCheckboxChange}>
                            <div className="w-full overflow-y-scroll max-h-96 ">
                                {tempAvailableScenarioNames.map((scenario: any, index: number) => (
                                    <div
                                        key={scenario.scenarioId}
                                        className={`font-medium p-2.5 text-greyFont w-full font-xs  border-borderLinePurpuleAlt ${
                                            tempAvailableScenarioNames.length - 1 !== index ? ' border-b' : ''
                                        }`}
                                    >
                                        <Checkbox
                                            value={scenario.scenarioId}
                                            checked={tempCheckedValues.includes(scenario.scenarioId)}
                                        >
                                            {scenario.name}
                                        </Checkbox>
                                    </div>
                                ))}
                            </div>
                        </Checkbox.Group>
                    ) : (
                        <div className="text-center h-32 flex items-center justify-center text-fontGray">
                            <span>No Plans available</span>
                        </div>
                    )}
                </div>
            </div>
            {tempCheckedValues.length > 0 && tempAvailableScenarioNames.length > 0 && (
                <div className="h-18 w-full bg-bgBackground p-2 rounded mt-2 overflow-y-scroll flex flex-wrap">
                    {tempAvailableScenarioNames
                        .filter((item: any) => tempCheckedValues.includes(item.scenarioId))
                        .map((item: any) => (
                            <div
                                key={item.scenarioId}
                                className="px-2 text-white mr-2 bg-purpleFont rounded h-6 font-xxs flex justify-center items-center mb-2"
                            >
                                <span className="mr-2">{item.name}</span>
                                <button type="button" onClick={() => handleRemoveSelection(item.scenarioId)}>
                                    <Cross stroke="white" />
                                </button>
                            </div>
                        ))}
                </div>
            )}

            <div className="flex justify-end pt-6 font-bold">
                <button
                    type="button"
                    className="mr-2 px-6 py-1 rounded cursor-pointer text-purpleFont bg-white border border-purpleFont font-xxs "
                    onClick={onResetClick}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="text-white  px-6 py-1 rounded cursor-pointer  bg-purpleFont border border-purpleFont font-xxs"
                    onClick={onApplyChangeHandler}
                >
                    Apply
                </button>
            </div>
        </StyledModal>
    );
};

export default AddScenarioModal;

const StyledModal = styled(Modal)`
    .ant-modal-title {
        ${tw`text-subHeaderColor`}
    }

    .ant-modal-close-x span svg {
        fill: '#140B1D';
    }

    .ant-modal-header {
        border: 0px;
    }

    .ant-modal {
        ${tw`w-1/3`}
    }

    .ant-modal-body {
        ${tw`pt-0`}
    }

    .ant-checkbox-checked .ant-checkbox-inner {
        ${tw`bg-purpleFont border-purpleFont`}
    }

    .ant-checkbox-wrapper {
        font-size: 11px;
    }

    .ant-checkbox-inner {
        height: 12px;
        width: 12px;
    }
`;

const LevelsFilterDiv = styled.div`
    ${tw`flex gap-2 items-center pt-4 pb-2`}
`;
